import React, { useContext, useEffect, useState } from 'react'
import { deleteField, updateDoc } from 'firebase/firestore'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { RoomType, RoomUserType } from '../../utils/types'
import { roomDoc } from '../../utils/firebase'
import { UserContext } from '../UserProvider'
import CardsList from './CardsList'
import Results from './Results'
import VotesTable from './VotesTable'
import { InlineEditable } from '../common/InlineEditable'
import { getPattern } from '../../utils/PatternGenerator'

const useStyles = makeStyles(theme => ({
  settings: {
    borderTop: `1px solid ${theme.palette.primary.main}`,
    [theme.breakpoints.up('md')]: {
      borderTop: 'none',
      borderLeft: `1px solid ${theme.palette.primary.main}`,
    },
  },
}))

type RoomProps = {
  room: RoomType
}

const Room: React.FC<RoomProps> = ({ room }) => {
  const classes = useStyles()
  const { user } = useContext(UserContext)
  const [leaving, setLeaving] = useState(false)

  const changeRoomTitle = (newTitle: string) => {
    updateDoc(roomDoc(room.id), { title: newTitle })
  }

  useEffect(() => {
    const removeUserFromRoom = () => {
      if (user && room) {
        setLeaving(true)
        updateDoc(roomDoc(room.id), { [`users.${user.id}`]: deleteField() })
      }
    }

    window.addEventListener('beforeunload', removeUserFromRoom)
    return () => {
      window.removeEventListener('beforeunload', removeUserFromRoom)
    }
  }, [user, room])

  useEffect(() => {
    if (!leaving && user && user.id && room && room.id) {
      if (room.users[user.id] === undefined) {
        updateDoc(roomDoc(room.id), { [`users.${user.id}`]: { id: user.id, name: user.name, observer: false, pattern: getPattern(room) } })
      } else {
        const updates: Record<string, any> = {};
        if (room.users[user.id].name !== user.name) {
          updates[`users.${user.id}.name`] = user.name
        }
        if (!room.users[user.id].pattern) {
          updates[`users.${user.id}.pattern`] = getPattern(room)
        }

        updateDoc(roomDoc(room.id), updates)
      }
    }
  }, [room, room.id, room.users, user, leaving])

  const allUsersHasEstimated = Object.values(room.users).filter(user => !user.observer).every((user: RoomUserType) => user.estimate !== undefined)
  const isObserver = Boolean(user && room.users[user.id]?.observer)

  return (
    <Grid container direction="row" justifyContent="center" spacing={3}>
      <Grid item xs={12}>
      </Grid>
      <Grid item xs={12} md={9}>
        <Typography variant="h4" align="center">
          <InlineEditable text={room.title} onSave={changeRoomTitle} />
        </Typography>
        {user && (
          <>
            <VotesTable room={room} showVotes={allUsersHasEstimated} />
            <CardsList room={room} user={user} observer={isObserver} ready={allUsersHasEstimated} />
          </>
        )}
      </Grid>
      <Grid item xs={12} md={3} className={classes.settings}>
        <Results room={room} allUsersHasEstimated={allUsersHasEstimated} />
      </Grid>
    </Grid>
  )
}

export default Room
